import type {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
} from "react";
import styled from "styled-components";

import { controlFocusRing } from "../foundations/controlStyles";
import { AnnotationContourLayer } from "./AnnotationContourLayer";
import { AnnotationPointMarkerGlyph } from "./AnnotationPointMarkerGlyph";
import { targetColorCss } from "./targetColor";
import type { AnnotationPointKind, AnnotationTarget, FrameTargetAnnotationDraft } from "./types";

export interface CoordinatePointPreview {
  readonly x: number;
  readonly y: number;
  readonly color: string;
  readonly kind: AnnotationPointKind | null;
}

const CoordinateGuide = styled.div`
  position: absolute;
  inset: 0;
  color: ${({ theme }) => theme.color.border.strong};
  pointer-events: none;

  svg {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
  }
`;

const CoordinateGuideLabel = styled.span<{ $corner: "start" | "end" }>`
  position: absolute;
  ${({ $corner }) =>
    $corner === "start" ? "top: 0.4rem; left: 0.4rem;" : "right: 0.4rem; bottom: 2.8rem;"}
  padding: 0.12rem 0.28rem;
  border: 1px solid ${({ theme }) => theme.color.border.divider};
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: ${({ theme }) => theme.color.bg.surfaceOverlay};
  color: ${({ theme }) => theme.color.text.default};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ theme }) => theme.typography.weight.medium};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  transform: scale(var(--c6-coordinate-guide-inverse-zoom, 1));
  transform-origin: ${({ $corner }) => ($corner === "start" ? "top left" : "bottom right")};
  white-space: nowrap;
`;

const Mask = styled.div`
  position: absolute;
  inset: 0;
  background: var(--c6-annotation-target-color);
  opacity: 0.18;
  pointer-events: none;
`;

const PointMarker = styled.button<{ $editable: boolean }>`
  position: absolute;
  width: 1.45rem;
  height: 1.45rem;
  padding: 0;
  border: 0;
  background: transparent;
  transform: translate(-50%, -50%) scale(var(--c6-annotation-marker-inverse-zoom, 1));
  transform-origin: center;
  color: var(--c6-annotation-target-color);
  cursor: ${({ $editable }) => ($editable ? "pointer" : "default")};

  &:focus-visible {
    ${controlFocusRing()}
  }
`;

const CoordinatePreviewMarker = styled.span`
  position: absolute;
  z-index: 3;
  display: grid;
  width: 1.45rem;
  height: 1.45rem;
  place-items: center;
  color: var(--c6-annotation-target-color);
  filter: drop-shadow(0 0 0.12rem ${({ theme }) => theme.color.bg.surfaceOverlay});
  transform: translate(-50%, -50%) scale(var(--c6-annotation-marker-inverse-zoom, 1));
  transform-origin: center;
  pointer-events: none;

  &::after {
    position: absolute;
    inset: -0.1rem;
    border: 2px dashed currentColor;
    border-radius: 50%;
    content: "";
  }
`;

const CoordinatePreviewCrosshair = styled.span`
  position: relative;
  display: block;
  width: 0.9rem;
  height: 0.9rem;

  &::before,
  &::after {
    position: absolute;
    top: 50%;
    left: 50%;
    border-radius: 999px;
    background: currentColor;
    box-shadow: 0 0 0 0.5px ${({ theme }) => theme.color.text.onControl};
    content: "";
    transform: translate(-50%, -50%);
  }

  &::before {
    width: 0.58rem;
    height: 1px;
  }

  &::after {
    width: 1px;
    height: 0.58rem;
  }
`;

const CoordinatePreviewReticle = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 1.9rem;
  height: 1.9rem;
  opacity: 0.88;
  transform: translate(-50%, -50%);

  &::before,
  &::after {
    position: absolute;
    content: "";
  }

  &::before {
    top: 50%;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(
      to right,
      currentColor 0 0.3rem,
      transparent 0.3rem 1.6rem,
      currentColor 1.6rem
    );
    transform: translateY(-50%);
  }

  &::after {
    top: 0;
    left: 50%;
    width: 1px;
    height: 100%;
    background: linear-gradient(
      to bottom,
      currentColor 0 0.3rem,
      transparent 0.3rem 1.6rem,
      currentColor 1.6rem
    );
    transform: translateX(-50%);
  }
`;

export function CapturedFrameAnnotationOverlay({
  guideVisible,
  preview,
  targets,
  inverseZoom,
  editable = true,
  removePointLabel,
  onRemovePoint,
}: {
  guideVisible: boolean;
  preview: CoordinatePointPreview | null;
  targets: readonly { target: AnnotationTarget; draft?: FrameTargetAnnotationDraft }[];
  inverseZoom: number;
  editable?: boolean;
  removePointLabel: string;
  onRemovePoint: (targetId: string, pointId: string) => void;
}) {
  return (
    <>
      {guideVisible ? (
        <CoordinateGuide
          aria-hidden="true"
          data-coordinate-guide=""
          style={
            {
              "--c6-coordinate-guide-inverse-zoom": String(inverseZoom),
            } as CSSProperties
          }
        >
          <svg viewBox="0 0 100 100" preserveAspectRatio="none">
            <rect
              x="0.5"
              y="0.5"
              width="99"
              height="99"
              fill="none"
              stroke="currentColor"
              vectorEffect="non-scaling-stroke"
              shapeRendering="crispEdges"
            />
            {[25, 50, 75].map((position) => (
              <g key={position}>
                <line
                  x1={position}
                  y1="0"
                  x2={position}
                  y2="100"
                  stroke="currentColor"
                  strokeDasharray="3 3"
                  vectorEffect="non-scaling-stroke"
                />
                <line
                  x1="0"
                  y1={position}
                  x2="100"
                  y2={position}
                  stroke="currentColor"
                  strokeDasharray="3 3"
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            ))}
          </svg>
          <CoordinateGuideLabel $corner="start">(0, 0)</CoordinateGuideLabel>
          <CoordinateGuideLabel $corner="end">(1, 1)</CoordinateGuideLabel>
        </CoordinateGuide>
      ) : null}
      {targets.map(({ target, draft }) => {
        const color = targetColorCss(target.color);
        return (
          <div key={target.id}>
            {draft?.contours ? (
              <AnnotationContourLayer
                contours={draft.contours}
                presentation="annotation"
                data-annotation-segmentation-target={target.id}
                style={{ "--c6-annotation-target-color": color } as CSSProperties}
              />
            ) : draft?.maskSource ? (
              <Mask style={{ "--c6-annotation-target-color": color } as CSSProperties} />
            ) : null}
            {draft?.points.map((point, pointIndex) => (
              <PointMarker
                key={point.id}
                as={editable ? "button" : "span"}
                $editable={editable}
                {...(editable ? { type: "button" as const } : { "aria-hidden": true })}
                data-annotation-editing-point=""
                aria-label={editable ? `${removePointLabel} ${pointIndex + 1}` : undefined}
                onPointerDown={
                  editable
                    ? (event: ReactPointerEvent<HTMLElement>) => event.stopPropagation()
                    : undefined
                }
                onClick={
                  editable
                    ? (event: ReactMouseEvent<HTMLElement>) => {
                        event.stopPropagation();
                        onRemovePoint(target.id, point.id);
                      }
                    : undefined
                }
                onPointerUp={
                  editable
                    ? (event: ReactPointerEvent<HTMLElement>) => event.stopPropagation()
                    : undefined
                }
                onContextMenu={
                  editable
                    ? (event: ReactMouseEvent<HTMLElement>) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }
                    : undefined
                }
                style={
                  {
                    left: `${point.x * 100}%`,
                    top: `${point.y * 100}%`,
                    "--c6-annotation-target-color": color,
                    "--c6-annotation-marker-inverse-zoom": String(inverseZoom),
                  } as CSSProperties
                }
              >
                <AnnotationPointMarkerGlyph kind={point.kind} variant="point" />
              </PointMarker>
            ))}
          </div>
        );
      })}
      {preview ? (
        <CoordinatePreviewMarker
          aria-hidden="true"
          data-coordinate-preview-kind={preview.kind ?? "pending"}
          data-coordinate-preview-marker=""
          style={
            {
              left: `${preview.x * 100}%`,
              top: `${preview.y * 100}%`,
              "--c6-annotation-target-color": preview.color,
              "--c6-annotation-marker-inverse-zoom": String(inverseZoom),
            } as CSSProperties
          }
        >
          <CoordinatePreviewReticle />
          {preview.kind ? (
            <AnnotationPointMarkerGlyph kind={preview.kind} variant="point" />
          ) : (
            <CoordinatePreviewCrosshair />
          )}
        </CoordinatePreviewMarker>
      ) : null}
    </>
  );
}
